<template>
  <section>
    <div class="py-5 dark-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-6 mb-4 mb-md-0 text-slide-in" :class="{ 'text-slide-in-active': textVisible }">
            <h2>Künstliche Intelligenz und Automatisierung</h2>
            <p class="mb-4">
              Die Bürokratie ist in fast allen Bereichen ständiger Begleiter und kostet wertvolle Zeit.
              Mit der Automatisierung von Betriebsabläufen und Verwaltung steigt die Produktivzeit bei
              hoher Belastung durch Vorgaben und händisch ausgeführte Prozesse.
            </p>
          </div>
          <div class="col-md-6 text-center">
            <img
                src="@/assets/ai/automation.jpg"
                alt="Motivation für Künstliche Intelligenz und Automatisierung"
                class="img-fluid rounded-img shadow-sm top-image"
                :class="{ 'slide-in-active': imageVisible }"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="py-5 light-section">
      <div class="container">
        <div class="row text-center mb-5">
          <div class="col-md-4 info-point">
            <i class="fas fa-folder-open fa-3x mb-3 icon"></i>
            <div class="info-point-header">Intelligente Datenverwaltung</div>
            <p>
              Künstliche Intelligenz hilft bei der Verarbeitung von Daten aus dem Betriebsablauf.
              <br>Egal ob Fortschrittsdokumentation, Fehlererkennung oder Stundenzettel -
              mit neuen Ansätzen im Bereich der Digitalisierung den persönlichen Aufwand zur
              Ordnung von Daten und Informationen aus dem Betriebsablauf reduzieren.
            </p>
          </div>
          <div class="col-md-4 info-point">
            <i class="fas fa-file-export fa-3x mb-3 icon"></i>
            <div class="info-point-header">Erstellung von Berichten</div>
            <p>
              Profitieren Sie von den Möglichkeiten zur automatisierten Erstellung von notwendiger Dokumentation,
              basierend auf Ihren Unternehmens- und Betriebsdaten zur täglichen Entlastung bei der Protokollierung.
              Überprüfen Sie Protokolle und Berichte anstatt Sie zu verfassen.
            </p>
          </div>
          <div class="col-md-4 info-point">
            <i class="fas fa-lightbulb fa-3x mb-3 icon"></i>
            <div class="info-point-header">Intelligente Datenanalyse</div>
            <p>
              Aus bestehenden Daten lassen sich oft Handlungsempfehlungen für die Zukunft ableiten.
              Probieren Sie es aus und lassen Sie sich überraschen, welche Informationen in Ihren Daten verborgen
              liegen.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="py-5 dark-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-md-6 text-center">
            <img
                src="@/assets/ai/solution.jpg"
                alt="Unsere KI- und Automatisierungslösung"
                class="img-fluid rounded-img shadow-sm solution-image"
                :class="{ 'slide-in-active': solutionImageVisible }"
            />
          </div>
          <div class="col-md-6 mb-4 mb-md-0 text-slide-up" :class="{ 'text-slide-up-active': solutionTextVisible }">
            <h2>Unsere Lösung</h2>
            <p class="mb-4">
              Wir analysieren Ihre Prozesse, Daten und Ärgernisse und entwickeln Lösungskonzepte, die auf Ihren Betrieb
              zugeschnitten sind. Im persönlichen Gespräch skizzieren wir Ihnen ein Digitalisierungskonzept um Ihre
              Arbeitslast
              zu reduzieren, ohne Qualitätsverluste in Kauf zu nehmen.
            </p>
            <router-link to="/contact" class="btn btn-primary btn-lg mt-2">
              Jetzt Kontakt aufnehmen
              <i class="fas fa-envelope"></i>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="py-5 light-section">
      <div class="container">
        <div class="row text-center">
          <div class="col-md-12">
            <h2 class="">Unsere Leistungen</h2>
            <p class="mb-4">
              Mit auf Ihr Unternehmen zugeschnittene KI-gestützte Automatisierungen reduzieren wir den täglichen
              Zeitaufwand für Dokumentation und Berichtswesen und helfen, das volle Potential Ihrer Betriebsdaten zu nutzen.
            </p>
          </div>
        </div>
        <div class="row text-center mt-4">
          <div class="col-md-4 info-point">
            <i class="fas fa-cogs fa-3x mb-3 icon"></i>
            <div class="info-point-header">Automatisches Datenmanagement</div>
            <p>Wir analysieren Ihre Dokumentationsprozesse und entwickeln zusammen mit Ihren Mitarbeitern Lösungen für
              die automatisierte Erfassung und Verwaltung Ihrer Betriebsdaten.
            </p>
          </div>
          <div class="col-md-4 info-point">
            <i class="fas fa-magnifying-glass fa-3x mb-3 icon"></i>
            <div class="info-point-header">KI-basierte Datenverarbeitung</div>
            <p>
              Wir bauen Systeme die aus Ihren Betriebsdaten die relevanten Informationen zu extrahieren und für Sie in
              Berichtsform konvertieren. Vollautomatisch und im Hintergrund. Von der Erstellung von Angeboten über die
              notwendige Fortschrittsprotokollierung bis hin zum Entwurf von Projektberichten.
            </p>
          </div>
          <div class="col-md-4 info-point">
            <i class="fas fa-brain fa-3x mb-3 icon"></i>
            <div class="info-point-header">KI-Agenten</div>
            <p>
              Künstliche Intelligenz kann noch mehr! Wir bauen individuelle KI-Agenten für Ihren Betrieb.
              Von Assistenzsystemen bis zu hochspezialisierten Datenanalysen - entdecken Sie mit uns, was für Ihren Betrieb möglich ist.
            </p>
          </div>
        </div>
      </div>
    </div>

  </section>
</template>

<script lang="ts">
import {defineComponent, ref, onMounted} from 'vue';

export default defineComponent({
  name: 'AIAutomationPage',
  setup() {
    const textVisible = ref(false);
    const imageVisible = ref(false);
    const solutionTextVisible = ref(false);
    const solutionImageVisible = ref(false);

    onMounted(() => {
      const observerOptions = {
        root: null,
        threshold: 0.1,
      };

      const observerCallback = (entries: IntersectionObserverEntry[]) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target.classList.contains('top-image')) {
              imageVisible.value = true;
            } else if (entry.target.classList.contains('solution-image')) {
              solutionImageVisible.value = true;
            } else if (entry.target.classList.contains('text-slide-in')) {
              textVisible.value = true;
            } else if (entry.target.classList.contains('text-slide-up')) {
              solutionTextVisible.value = true;
            }
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, observerOptions);

      document.querySelectorAll('.text-slide-in, .top-image, .solution-image, .text-slide-up').forEach(element => {
        observer.observe(element);
      });
    });

    return {textVisible, imageVisible, solutionTextVisible, solutionImageVisible};
  },
});
</script>

<style scoped lang="less">
@import '@/assets/style.less';
@import '@/assets/animations.less';

.top-image {
  margin-top: 2rem;
}
</style>
